import React from 'react'
import { MaybeLoaded } from 'json-react-layouts-data-loader'

import {
    Props as SectionHeaderProps,
    SectionHeader,
} from 'component-library/section-header/SectionHeader/SectionHeader'
import { StyledUniform, StyledUniformCardcontainer, StyledHeader } from './Uniform.styled'
import { Portrait, CardOrientationOptions } from 'component-library/cards/Portrait/Portrait'
import { getCardItemProps, LayoutName } from 'component-library/cards/Portrait/Portrait.layouts'
import { CardSwitcher } from 'component-library/__helpers/CardSwitcher'

import { ImpressionAvailable } from 'component-library/__helpers/impression-available-helper'
import { invertMaybeLoadedItems } from '../helpers/loading'
import { StyledNovember } from '../November/StyledNovember'
import { OverrideThemeSection } from 'component-library/__helpers/override-theme-section'
import { CollectionEvent, createCollectionAvailableEvent } from 'common/events/collection-events'
import { CardItem, PublicationCardItem } from 'common/client-data-types/card-types'
import { Section } from 'component-library/__styling/settings/sections'
import { ThemeMargins } from 'component-library/__styling/settings/metrics'

export type ListLength = 1 | 2 | 3 | 4 | 5 | 6

export type NovemberLayout = {
    type: 'november'
    listLength: ListLength
    hasTimestamp?: boolean
    sectionHeader?: SectionHeaderProps
    noStretch?: boolean
}

export type UniformLayout = LayoutName[] | NovemberLayout

export type UniformLayouts = [UniformLayout, UniformLayout, UniformLayout, UniformLayout]

export interface Props {
    className?: string
    section?: Section
    sectionHeader?: SectionHeaderProps
    onEvent: (event: CollectionEvent) => void
    verticalSpacing?: keyof ThemeMargins | undefined
    disableImageLazyLoad?: boolean
    cardLayout: UniformLayouts
    cardOrientation?: CardOrientationOptions
    expectedCards: number
    items: MaybeLoaded<CardItem[]>
}

export type UniformPropsInternal = Props

export type CollectionItemProps = Array<undefined | PublicationCardItem>

export function isNovemberLayout(layout: UniformLayout): layout is NovemberLayout
export function isNovemberLayout(layout: LayoutName | NovemberLayout): layout is NovemberLayout
export function isNovemberLayout(layout: any) {
    return typeof layout === 'object' && 'type' in layout && layout.type === 'november'
}

export function calculateLayoutsLength(layouts: UniformLayout[]): number {
    return layouts.reduce((accumulator, layout) => {
        const addition = isNovemberLayout(layout) ? layout.listLength : layout.length
        return accumulator + addition
    }, 0)
}

export const Uniform: React.FC<UniformPropsInternal> = props => {
    const {
        onEvent,
        verticalSpacing,
        className,
        section,
        sectionHeader,
        cardLayout,
        expectedCards,
    } = props

    const renderUniform = () => {
        const items = invertMaybeLoadedItems(props.items, expectedCards)

        return (
            <ImpressionAvailable
                loading={!props.items.loaded}
                available={() => {
                    if (!props.items.loaded) {
                        console.warn('Available should never be called when loading is true')
                        return
                    }
                    props.onEvent(createCollectionAvailableEvent(props.items.result, 'Uniform'))
                }}
            >
                {ref => (
                    <div ref={ref}>
                        {sectionHeader && items && (
                            <StyledHeader>
                                <SectionHeader
                                    heading={sectionHeader.heading}
                                    section={sectionHeader.section}
                                    headingUrl={sectionHeader.headingUrl}
                                    masthead={sectionHeader.masthead}
                                    navLinks={sectionHeader.navLinks}
                                    onClick={sectionHeader.onClick}
                                    canGrow={true}
                                />
                            </StyledHeader>
                        )}
                        <StyledUniform verticalSpacing={verticalSpacing} className={className}>
                            {cardLayout.map((layout, layoutIndex) => {
                                const prevLayouts = cardLayout.slice(0, layoutIndex)
                                const prevLayoutsLength = calculateLayoutsLength(prevLayouts)

                                return (
                                    <StyledUniformCardcontainer key={layoutIndex}>
                                        {isNovemberLayout(layout) ? (
                                            <StyledNovember
                                                hasBackground
                                                disableImpressionEvent={true}
                                                cardNumberOffset={prevLayoutsLength}
                                                sectionHeader={layout.sectionHeader}
                                                section={section}
                                                onEvent={onEvent}
                                                timestamp={'list-style'}
                                                noStretch={layout.noStretch}
                                                items={
                                                    props.items.loaded
                                                        ? {
                                                              loaded: true,
                                                              result: props.items.result.slice(
                                                                  prevLayoutsLength,
                                                                  prevLayoutsLength +
                                                                      layout.listLength
                                                              ),
                                                          }
                                                        : { loaded: false }
                                                }
                                                expectedCards={layout.listLength}
                                            />
                                        ) : (
                                            layout.map((columnLayout, columnIndex) => {
                                                const columnLayoutLength =
                                                    calculateLayoutsLength([
                                                        layout.slice(0, columnIndex),
                                                    ]) + prevLayoutsLength
                                                const cardNumber = columnLayoutLength + 1

                                                const cardOrientation = props.cardOrientation
                                                const item = items[columnLayoutLength] || {
                                                    loaded: false,
                                                }

                                                return (
                                                    <CardSwitcher
                                                        key={columnLayoutLength}
                                                        item={item}
                                                        cardContext="uniform-portrait"
                                                        cardNumber={cardNumber}
                                                        publicationCard={publicationItem => (
                                                            <Portrait
                                                                fixedRatio={'16:9'}
                                                                {...getCardItemProps(
                                                                    publicationItem,
                                                                    columnLayout,
                                                                    {
                                                                        onEvent,
                                                                    },
                                                                    cardNumber,
                                                                    cardOrientation
                                                                )}
                                                            />
                                                        )}
                                                    />
                                                )
                                            })
                                        )}
                                    </StyledUniformCardcontainer>
                                )
                            })}
                        </StyledUniform>
                    </div>
                )}
            </ImpressionAvailable>
        )
    }

    return section ? (
        <OverrideThemeSection section={section}>{renderUniform()}</OverrideThemeSection>
    ) : (
        renderUniform()
    )
}

Uniform.displayName = 'Uniform'
